import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Image as ImageIcon, RotateCcw } from 'lucide-react';
import clsx from 'clsx';

export type ImageVariant = { id: string; url: string; prompt?: string; seed?: string };

export function ImageVariantGrid({
  variants,
  selected,
  onSelect,
  onRegenerate,
}: {
  variants: ImageVariant[];
  selected: string | null;
  onSelect: (id: string) => void;
  onRegenerate?: () => void;
}) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {variants.map((v, i) => (
        <VariantTile
          key={v.id}
          v={v}
          index={i}
          isSelected={selected === v.id}
          onClick={() => onSelect(v.id)}
          onRegenerate={onRegenerate}
        />
      ))}
    </div>
  );
}

function VariantTile({
  v,
  index,
  isSelected,
  onClick,
  onRegenerate,
}: {
  v: ImageVariant;
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onRegenerate?: () => void;
}) {
  const [loaded, setLoaded] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="relative group"
    >
      <button
        onClick={onClick}
        className={clsx(
          'w-full aspect-square rounded-2xl overflow-hidden border-2 transition relative',
          isSelected
            ? 'border-etsy shadow-[0_0_0_4px_rgba(241,100,30,0.12),0_8px_24px_-6px_rgba(241,100,30,0.35)]'
            : 'border-cream-300 hover:border-etsy-200',
        )}
      >
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center text-ink-400 bg-cream-200">
            <ImageIcon size={20} className="animate-pulse-soft" />
          </div>
        )}
        <img
          src={v.url}
          alt={v.prompt ?? 'variant'}
          onLoad={() => setLoaded(true)}
          className={clsx('w-full h-full object-cover relative transition-opacity', loaded ? 'opacity-100' : 'opacity-0')}
        />

        {/* Variant index — pill in the top-left */}
        <div className="absolute top-2.5 left-2.5 id-chip bg-white/85 backdrop-blur-sm border-white/40">
          v{index + 1}
        </div>

        {/* Selected checkmark — Etsy orange */}
        <AnimatePresence>
          {isSelected && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 400, damping: 24 }}
              className="absolute top-2.5 right-2.5 h-7 w-7 rounded-full bg-etsy flex items-center justify-center shadow-[0_4px_12px_-2px_rgba(241,100,30,0.55)]"
            >
              <Check size={13} className="text-white" strokeWidth={3} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Regen on hover — cream with etsy orange icon */}
        {onRegenerate && (
          <button
            onClick={(e) => { e.stopPropagation(); onRegenerate(); }}
            className="absolute bottom-2.5 right-2.5 h-8 w-8 rounded-lg bg-white/90 backdrop-blur-sm border border-cream-300 text-ink-700 opacity-0 group-hover:opacity-100 transition flex items-center justify-center hover:bg-etsy hover:text-white hover:border-etsy"
          >
            <RotateCcw size={12} />
          </button>
        )}
      </button>
    </motion.div>
  );
}
